/* eslint-disable-off functional/immutable-data */
/* eslint-disable-off functional/prefer-readonly-type */
import _findIndex from 'lodash/findIndex';
import _orderBy from 'lodash/orderBy';
import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Spinner } from 'react-bootstrap';
import Select, { components } from 'react-select';
import type { GroupBase, OptionProps, ValueContainerProps } from 'react-select';
import { LoadingIconProps } from 'react-select/src/components/indicators';
import useEffectOnce from 'react-use/esm/useEffectOnce';

export type OptionType = {
  readonly label: string;
  readonly value: number;
  readonly isActive: boolean;
  readonly notes?: string;
  readonly color?: string;
  readonly units?: string;
};

const LoadingIndicator = (props: LoadingIconProps<OptionType, false>) => {
  return <Spinner animation="border" size="sm" className="m-1" variant="primary" />;
};

// function Menu(props: MenuProps<OptionType>) {
//   return <Dropdown.Menu {...props.innerProps}>{props.children}</Dropdown.Menu>;
// }

// function SingleValue(props: SingleValueProps<OptionType>) {
//   return <div {...props.innerProps}>{props.children}</div>;
// }

const HiddenMultiValue = () => null;

function createCheckboxOption() {
  return (props: OptionProps<OptionType, true, GroupBase<OptionType>>) => (
    <components.Option {...props}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
        <input type="checkbox" checked={props.isSelected} readOnly tabIndex={-1} />
        <span>{props.label}</span>
      </div>
    </components.Option>
  );
}

function createSummaryValueContainer(
  formatMultiSummary: (selected: readonly OptionType[]) => string,
  controlHeight: number,
) {
  return (props: ValueContainerProps<OptionType, true, GroupBase<OptionType>>) => {
    const selected = props.getValue();
    if (selected.length > 0) {
      const childArray = React.Children.toArray(props.children);
      const input = childArray[childArray.length - 1];
      return (
        <components.ValueContainer {...props}>
          <div
            style={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              color: '#495057',
              flex: 1,
              lineHeight: `${controlHeight}px`,
            }}
          >
            {formatMultiSummary(selected)}
          </div>
          {input}
        </components.ValueContainer>
      );
    }
    return <components.ValueContainer {...props}>{props.children}</components.ValueContainer>;
  };
}

const formatOptionLabel = ({ value, label, isActive, notes, color }) => (
  <div style={{ display: 'flex', alignItems: 'center' }}>
    {isActive ? (
      <>
        <div style={{ color: color ? color : undefined }}>{label}</div>
        {/* Το ms-3 αφήνει κενό στο chip ακόμα και χωρίς notes */}
        {notes ? <small className="ms-3 text-muted">{notes}</small> : null}
      </>
    ) : (
      <>
        <div style={{ color: color ? color : undefined, fontStyle: 'italic' }}>{label}</div>
        {notes ? <small className="ms-3 text-muted">{notes}</small> : null}
      </>
    )}
  </div>
);

//-------------------------------------------------------------------------------

type Props = {
  readonly id?: string;
  readonly className?: string;
  /** Lazy Query for records  id-> data*/
  readonly hook: () => any;
  readonly disabled?: boolean;
  readonly required?: boolean;
  readonly recordLabel?: string;
  readonly recordLabel2?: string;
  readonly recordLabel3?: string;
  readonly formatLabel?: (record: any) => string;
  readonly where?: string;
  /** Default Input Box Value if selected is undefined */
  readonly placeholder: string | undefined;
  readonly isInvalid?: boolean;
  readonly disableInactives?: boolean;
  readonly isMulti?: boolean;
  /** value number  */
  readonly value: number | null | any;
  readonly label: string | null;
  readonly minMenuHeight?: number;
  readonly maxMenuHeight?: number;
  /** On change value */
  readonly onChange?: (option: OptionType | ReadonlyArray<OptionType>) => void;
  readonly onBlur?: () => void;
  readonly height?: number;
  readonly sortOptionValues?: boolean;
  readonly multiDisplayMode?: 'chips' | 'summary';
  readonly formatMultiSummary?: (selected: readonly OptionType[]) => string;
  readonly showOptionCheckbox?: boolean;
};

/*
  Effects:
  1: Once
  2: data
  3: value
  4: where
*/

const SelectDB: FC<Props> = ({
  id = 'sbdb',
  className,
  hook,
  disabled = false,
  required = false,
  recordLabel = 'description',
  recordLabel2 = 'description2',
  recordLabel3 = 'description3',
  formatLabel = null,
  where = undefined,
  placeholder,
  isInvalid = false,
  disableInactives = false,
  value,
  label,
  minMenuHeight = 140,
  maxMenuHeight = 300,
  onChange = null,
  onBlur = null,
  isMulti = false,
  height = 22,
  sortOptionValues = true,
  multiDisplayMode = 'chips',
  formatMultiSummary = undefined,
  showOptionCheckbox = false,
}) => {
  //
  //-------------------- Body -----------------------------------------------
  //
  const [get, { data, loading }] = hook();
  const queryDone = useRef<boolean>(false);
  const initValue = useRef<number | null | ReadonlyArray<OptionType>>(null);
  const initLabel = useRef<string | null>(null);
  // const [selectValue, setSelectValue] = useState<OptionType | [OptionType]>(
  const mapValueToOptionArray = (arr: any[]): OptionType[] =>
    (Array.isArray(arr) ? arr : [])
      .map((v) => {
        if (typeof v === 'number' || (typeof v === 'string' && v !== '')) {
          return {
            value: Number(v),
            label: '',
            isActive: true,
            notes: undefined,
            color: undefined,
            units: undefined,
          };
        }
        if (v && typeof v === 'object' && v.value != null && v.value !== '') {
          return {
            value: v.value as number,
            label: (v.label as string) ?? '',
            isActive: true,
            notes: undefined,
            color: undefined,
            units: undefined,
          };
        }
        return null;
      })
      .filter(Boolean) as OptionType[];

  // Στο multi, αν το Formik ξεκινήσει χωρίς array (νέα εγγραφή), μην φτιάξεις κενό chip
  const [selectValue, setSelectValue0] = useState<any>(
    isMulti
      ? mapValueToOptionArray(value)
      : {
          value: value,
          label: label,
          isActive: true,
          notes: undefined,
          color: undefined,
          units: undefined,
        },
  );
  const setSelectValue = (value, pos) => {
    // console.log(
    //   `[SelectDB]:${id} SetSelectValue from ${pos} value=`,
    //   value,
    //   ' oldValue=',
    //   selectValue
    // );
    if (value !== selectValue) setSelectValue0(value);
  };

  const [options, setOptions] = useState<ReadonlyArray<OptionType>>([]);
  const [initialWhere, setInitialWhere] = useState(where);
  /**
   * * 1. Τη πρώτη φορά αν έχω value αλλά δεν έχω Label τοτε κάνω request
   */
  useEffectOnce(() => {
    setInitialWhere(where);

    initValue.current = value;
    initLabel.current = label;
    // const opt = { value: value, label: label, isActive: true };
    // setSelectValue(opt);

    const opt = isMulti
      ? mapValueToOptionArray(value)
      : {
          value: value,
          label: label,
          isActive: true,
          notes: undefined,
          color: undefined,
          units: undefined,
        };
    setSelectValue(opt, 'Effect 1');

    // console.log(`[SelectDB]:${id} Value:${value} Label:${label} getting from ${recordLabel}`);
    // Αν υπάρχει value χωρις label τότε διάβασε τα options
    if (!Array.isArray(value) && !!value && !label) {
      console.log(`[SelectDB]:${id} make DB request value:${value}`);
      doQuery('Effect 1');
    }
    // isMulti με array of ids: φόρτωσε options ώστε να λυθούν τα labels
    if (isMulti && Array.isArray(value) && value.length > 0) {
      doQuery('Effect 1 multi');
    }
  });

  /**
   * * 2. Οταν ολοκληρωθεί το διάβασμα από τη βάση γεμίζει ο πίνακας
   * *    των επιλογών και ενημερώνω το initLabel αν υπάρχει initValue
   */
  useEffect(() => {
    console.log(`[SelectDB]:${id} useEffect 2 triggered`);
    if (!!data && options.length === 0) {
      const propNames = Object.getOwnPropertyNames(data);
      const records =
        !!data[propNames[0]] && !!data[propNames[0]]['data'] ? data[propNames[0]]['data'] : [];

      // Συμβολοσειρά από null/undefined με + γίνεται "null" — για κενό prefix δείχνουμε παύλα (επιχειρησιακά πιο καθαρό UI)
      const fmtSeg = (v: unknown) => (v != null && v !== '' ? String(v) : '-');

      const newOptions: readonly OptionType[] = records.map((record) => {
        let label: string;
        if (formatLabel) label = formatLabel(record);
        else
          label =
            fmtSeg(record[recordLabel]) +
            (record[recordLabel2] ? ' - ' + fmtSeg(record[recordLabel2]) : '') +
            (record[recordLabel3] ? ' - ' + fmtSeg(record[recordLabel3]) : '');
        return {
          value: record.id,
          label: label,
          isActive: record?.isActive ?? true,
          color: record?.color,
          units: record?.units ? record?.units : record?.product?.units ?? '',
        };
      });
      if (sortOptionValues === false) {
        setOptions(newOptions);
      } else {
        setOptions(_orderBy(newOptions, ['isActive', 'label'], ['desc', 'asc']));
      }
      queryDone.current = true;
      //console.log(`[SelectDB]:${id} new Options:`, newOptions);
      /**
       *  Τωρα που εχουμε τα options, αν υπάρχει αρχική τιμή βρίσκω το label
       */
      if (!!initValue.current && !initLabel.current && selectValue?.value === initValue.current) {
        const idx = _findIndex(newOptions, ['value', initValue.current]);
        if (idx >= 0) {
          initLabel.current = newOptions[idx].label;
          setSelectValue({ ...newOptions[idx] }, 'Effect 2.1');
        }
        return;
      }

      if (!!selectValue?.value && !selectValue?.label) {
        const idx = _findIndex(newOptions, ['value', selectValue?.value]);
        if (idx >= 0) {
          const newValue = { ...newOptions[idx] };
          setSelectValue(newValue, 'Effect 2.2');
        }
      }

      // isMulti: resolve ids to full options when options loaded
      if (
        isMulti &&
        Array.isArray(selectValue) &&
        selectValue.some((item) => item?.value != null && !item?.label)
      ) {
        const resolved = selectValue.map((item) => {
          if (item?.label) return item;
          const idx = _findIndex(newOptions, ['value', item?.value]);
          return idx >= 0 ? { ...newOptions[idx] } : item;
        });
        setSelectValue(resolved, 'Effect 2.3 multi');
      }
    }
    // eslint-disable-off-next-line-off react-hooks/exhaustive-deps
  }, [data]);

  /**
   * * 3. Αν υπάρχουν αλλαγές στο value από Props
   */
  useEffect(() => {
    if (isMulti) {
      const next = mapValueToOptionArray(value);
      const current = Array.isArray(selectValue) ? selectValue : [];
      const nextIds = next.map((o) => o.value).join(',');
      const currentIds = current.map((o) => o.value).join(',');
      if (nextIds === currentIds) return;
      if (next.length === 0) {
        setSelectValue([], 'Effect 3 multi empty');
        return;
      }
      if (options.length > 0) {
        const resolved = next.map((item) => {
          const idx = _findIndex(options, ['value', item.value]);
          return idx >= 0 ? { ...options[idx] } : item;
        });
        setSelectValue(resolved, 'Effect 3 multi');
      } else {
        setSelectValue(next, 'Effect 3 multi pending');
        doQuery('Effect 3 multi');
      }
      return;
    }

    if (value !== selectValue?.value) {
      //console.log('[SelectDB] New value prop:', value);
      // Ο χρήστης έσβησε το πεδίο, η τιμή είναι null
      if (value === null) {
        const opt = isMulti ? [] : { value: null, label: null, isActive: false };
        setSelectValue(opt, 'Effect 3.1');
        return;
      }
      // Ο χρήστης Αρχικοποίησε το πεδίο, ολα γυρνούν στα initValues
      if (value === initValue.current) {
        const opt = isMulti
          ? (initValue.current as readonly [OptionType])
          : {
              value: initValue.current,
              label: initLabel.current,
              isActive: true,
            };
        setSelectValue(opt, 'Effect 3.2');
        return;
      }
      // Αλλαξε η τιμή όμως δεν έχουμε τα options, θα πρέπει να δωθεί προσωρινά τιμή στο
      // selectValue μονο μέ το value και να εκτελεστεί το query για να έρθουν και τα labels
      // Στη φάση αυτή δεν εκτελώ το τελικό onChange, αφήνω το effect που θα αναλάβει το διάβασμα
      // των data να το κάνει.
      if (!queryDone.current) {
        const opt = { value: value, label: null, isActive: false };
        setSelectValue(opt, 'Effect 3.3');
        doQuery('Effect 3');
        return;
      }
      // Αλλαξε η τιμή και έχουμε τα options. Ψάχνω και αντιγράφω από τα options
      const idx = _findIndex(options, ['value', value]);
      if (idx >= 0) {
        const opt = {
          value: value,
          label: options[idx].label,
          isActive: options[idx].isActive,
          notes: options[idx].notes,
          color: options[idx].color,
          units: options[idx].units,
        };
        setSelectValue(opt, 'Effect 3.4');
        return;
      }
      // Δεν βρέθηκε η τιμή στα options. Προφανώς πρόκειται περί λάθους. Τα μηδενίζω όλα
      const opt = isMulti ? [] : { value: null, label: null, isActive: false };
      setSelectValue(opt, 'Effect 3.5');
      return;
    }

    // eslint-disable-off-next-line-off react-hooks/exhaustive-deps
  }, [value]);

  /**
   * * 4. Αν υπάρχουν αλλαγές στο where
   */
  useEffect(() => {
    // Μηδένισε επιλογή και καθάρισε τα options
    //$$##
    console.log(
      `[SelectDB]:${id} Effect 4 [where] value=`,
      value,
      ' where=',
      where,
      'options=',
      options,
      ' initialWhere=',
      initialWhere,
    );
    //initialWhere <> where
    if (where !== initialWhere) {
      console.log(
        `[SelectDB]:${id} Effect 4 [where changed from init] where=`,
        where,
        ' initialWhere=',
        initialWhere,
      );
      setSelectValue(isMulti ? [] : undefined, 'Effect 4 [where]');
      setOptions([]);
    }
    // //setSelectValue(isMulti ? [] : undefined, 'Effect 4 [where]');
    // setSelectValue(value, 'Effect 4 [where]');
    // setOptions([]);
    // eslint-disable-off-next-line-off react-hooks/exhaustive-deps
  }, [where]);
  //
  //-------------------- Handlers -------------------------------------------
  //

  /**
   * * 1. To handler της αλλαγής τιμής από το select
   */
  const handleChange = useCallback(
    (selectedOption: OptionType, action) => {
      console.log(`[SelectDB]:${id} handleChange`, selectedOption, action);

      if (action.action === 'clear') {
        const opt = isMulti
          ? []
          : {
              value: null,
              label: null,
              isActive: false,
            };
        setSelectValue(opt, 'HandleChange.1');
        !!onChange && onChange(opt);
        return;
      }

      // Στο multi το selectedOption είναι πίνακας· μην το συγκρίνεις με initValue
      if (isMulti) {
        const opt = Array.isArray(selectedOption)
          ? selectedOption
          : selectedOption
          ? [selectedOption]
          : [];
        setSelectValue(opt, 'HandleChange.multi');
        !!onChange && onChange(opt);
        return;
      }

      if (selectedOption?.value === initValue.current) {
        const opt = {
          value: initValue.current,
          label: initLabel.current,
          isActive: true,
        };
        // console.log(`[SelectDB]:${id} onChange value:${initValue.current}`);
        setSelectValue(opt, 'HandleChange.2');
        !!onChange && onChange(opt);
        return;
      } else {
        const opt = isMulti
          ? selectedOption
          : {
              value: selectedOption.value,
              label: selectedOption.label,
              isActive: selectedOption.isActive,
              notes: selectedOption.notes,
              color: selectedOption.color,
              units: selectedOption.units,
            };
        // console.log(`[SelectDB]:${id} onChange newValue:${selectedOption.value}`);
        setSelectValue(opt, 'HandleChange.3');
        !!onChange && onChange(opt);
        return;
      }
    },
    [onChange, isMulti],
  );

  /**
   * * 2. To get του query
   */
  const doQuery = (pos) => {
    console.log(`[SelectDB]:${id} doQuery`, pos, where);
    if (where !== undefined) {
      get({ variables: { where } });
    } else {
      get();
    }
  };

  //
  //------------------ STYLING ----------------------------------
  //

  const styles = useMemo(
    () => ({
      container: (base, state) => ({
        ...base,
        flex: 1,
        //display: 'inline-block',
        // width: '250px',
        minHeight: '1px',
        //height: '26px',
        //marginBottom: '8px',
        //textAlign: 'left',
        // border: 'none',
      }),
      control: (base, state) => ({
        ...base,
        height: `${height}px`,
        minHeight: `${height}px`,
        alignItems: 'center',
        backgroundColor: disabled ? '#e9ecef' : required ? 'lightYellow' : '#fff',
        borderWidth: '1px',
        boxShadow: !state.isFocused
          ? 'inset 0 1px 1px #00000013'
          : isInvalid
          ? '0 0 0 .2rem #dc354560'
          : '0 0 0 .2rem #007bff40',

        borderColor: isInvalid ? '#dc3545' : '#ced4da',
        '&:hover': {
          borderColor: isInvalid ? '#dc3545' : '#ced4da',
          //   borderColor: state.isFocused ? '#ced4da' : isInvalid ? '#dc3545' : '#ced4da',
        },
      }),
      input: (base, state) => ({
        ...base,
        minHeight: '1px',
        margin: 0,
        padding: 0,
      }),
      indicatorsContainer: (base, state) => ({
        ...base,
        height: `${height - 2}px`,
        minHeight: '1px',
        alignItems: 'center',
      }),
      dropdownIndicator: (base, state) => ({
        ...base,
        paddingTop: '0',
        paddingBottom: '0',
        minHeight: '1px',
        // color: '#757575',
      }),
      indicatorSeparator: (base, state) => ({
        ...base,
        minHeight: '1px',
        height: `${height - 2}px`,
        marginTop: '0px',
        marginBottom: '0px',
      }),
      clearIndicator: (base, state) => ({
        ...base,
        paddingTop: '0',
        paddingBottom: '0',
        minHeight: '1px',
      }),
      valueContainer: (base, state) => ({
        ...base,
        minHeight: '1px',
        // Ύψος εσωτερικού πεδίου (χωρίς τα 1px border) για κατακόρυφο κεντράρισμα
        height: `${height - 2}px`,
        paddingTop: '0',
        paddingBottom: '0',
        display: 'flex',
        alignItems: 'center',
        flexWrap: 'nowrap',
      }),
      singleValue: (base, state) => ({
        ...base,
        color: '#495057',
        minHeight: '1px',
        paddingTop: '1',
        paddingBottom: '1',
      }),
      // Χωρίς κατακόρυφο margin ώστε τα chips να κεντράρονται στο πεδίο
      multiValue: (base, state) => ({
        ...base,
        marginTop: 0,
        marginBottom: 0,
        marginRight: '2px',
        alignItems: 'center',
      }),
      // Σφιχτό chip ώστε να χωράνε περισσότερες τιμές στη γραμμή
      multiValueLabel: (base, state) => ({
        ...base,
        paddingTop: 0,
        paddingBottom: 0,
        paddingLeft: '4px',
        paddingRight: '2px',
        display: 'flex',
        alignItems: 'center',
      }),
      multiValueRemove: (base, state) => ({
        ...base,
        paddingLeft: '2px',
        paddingRight: '2px',
        display: 'flex',
        alignItems: 'center',
      }),
      loadingIndicator: (base, state) => ({
        ...base,
        paddingTop: '1',
        paddingBottom: '1',
        minHeight: '1px',
        color: '#007bff80',
      }),
      noOptionsMessage: (base, state) => ({
        ...base,
        color: '#dc3545',
      }),
      menuPortal: (base) => ({
        ...base,
        zIndex: 9999,
      }),
      menu: (base, state) => ({
        ...base,
        zIndex: 9999,
        marginTop: '0px',
        borderTopLeftRadius: '0px',
        borderTopRightRadius: '0px',
      }),
      option: (base, state) => ({
        ...base,
        paddingTop: '4px',
        paddingBottom: '4px',
      }),
    }),
    [disabled, isInvalid, required, height],
  );

  const useSummaryMultiDisplay = isMulti && multiDisplayMode === 'summary';
  const useOptionCheckbox = showOptionCheckbox || useSummaryMultiDisplay;

  const selectComponents = useMemo(() => {
    const baseComponents: Record<string, unknown> = {
      LoadingIndicator: LoadingIndicator as any,
    };

    if (isMulti && useOptionCheckbox) {
      baseComponents.Option = createCheckboxOption();
    }

    if (useSummaryMultiDisplay) {
      baseComponents.MultiValue = HiddenMultiValue;
      baseComponents.ValueContainer = createSummaryValueContainer(
        formatMultiSummary ??
          ((selected) =>
            selected.length === 1 ? selected[0].label : `${selected.length} selected`),
        height,
      );
    }

    return baseComponents;
  }, [formatMultiSummary, height, isMulti, useOptionCheckbox, useSummaryMultiDisplay]);

  //
  //-------------------- Render ----------------------------------------------
  //
  // useWhyDidYouUpdate(`[SelectDB]:${id} `, {
  //   className,
  //   hook,
  //   disabled,
  //   recordLabel,
  //   where,
  //   placeholder,
  //   isInvalid,
  //   value,
  //   label,
  //   minMenuHeight,
  //   maxMenuHeight,
  //   onChange,
  //   onBlur,
  //   get,
  //   data,
  //   loading,
  //   initValue: initValue.current,
  //   initLabel: initLabel.current,
  //   selectValue,
  //   options,
  // });

  // console.log('$$$ selectValue $$$', selectValue);
  return (
    <Select
      className={className}
      styles={styles}
      isDisabled={disabled}
      isSearchable={true}
      isClearable={true}
      placeholder={placeholder}
      onMenuOpen={() => doQuery('OnMenuOpen')}
      onFocus={() => doQuery('OnFocus')}
      value={selectValue}
      // value={[{ value: 1, label: 'one' }]}
      // defaultValue={selectValue}
      onChange={handleChange}
      onBlur={onBlur}
      options={options}
      isLoading={loading}
      loadingMessage={() => null}
      noOptionsMessage={() => null}
      menuPlacement="auto"
      menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
      menuPosition="fixed"
      maxMenuHeight={maxMenuHeight}
      minMenuHeight={minMenuHeight}
      components={selectComponents}
      // Με checkboxes τα επιλεγμένα πρέπει να μένουν ορατά στο menu
      hideSelectedOptions={useOptionCheckbox ? false : undefined}
      closeMenuOnSelect={useOptionCheckbox ? false : undefined}
      formatOptionLabel={formatOptionLabel}
      isOptionDisabled={(option) => option.isActive === false && disableInactives}
      isMulti={isMulti}
    />
  );
};

export default SelectDB;
